import React, { createContext, useEffect, useMemo, useState } from 'react';
import { node } from 'prop-types';
import * as R from 'ramda';
import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import http from 'utils/http';
import { affiliatePermissions } from 'utils/currentUserRoles';
import { isAuthenticated } from 'utils/authService';
import { updateAffiliate } from 'authentication/authenticationSlice';
import { useDispatch } from 'react-redux';
import { theme } from 'library';

export const AuthContext = createContext({
  affiliateRoles: [],
  affiliates: [],
  clearTimeouts: () => {},
  currentSelectedAffiliate: 0,
  roles: [],
  setCurrentSelectedAffiliate: () => {},
  userProfile: {},
});

export const AuthProvider = ({ children }) => {
  const [currentSelectedAffiliate, setSelectedAffiliate] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const dispatch = useDispatch();
  const [isError, setError] = useState(false);

  const { profile } = isAuthenticated();

  // This is the global current affiliate that is updated when the user changes
  // affiliates, and this removes the currentAffilate from redux states
  const currentAffiliate = affiliates[currentSelectedAffiliate];

  useEffect(() => {
    let errorTimeout;
    if (!R.isNil(isAuthenticated()) && R.isEmpty(affiliates)) {
      let controller;
      if (controller) controller.abort();
      controller = new AbortController();
      const { signal } = controller;

      http
        .get('/subscription-plans/get-user-affiliates', { signal })
        .then(({ data }) => {
          if (data.length === 0) {
            setError(true);
          } else {
            setAffiliates(data);
            setError(false);
            sessionStorage.setItem('affiliates', JSON.stringify(data));
          }
        })
        .catch(errorinfo => {
          // Delay error handling to avoid showing dialog for temporary 401s
          errorTimeout = setTimeout(() => {
            if (errorinfo.response?.status === 403 || errorinfo.response?.status === 401) {
              setError(true);
            }
          }, 2000);
        });
    }

    // Always return cleanup function
    return () => {
      if (errorTimeout) clearTimeout(errorTimeout);
    };
  }, [affiliates]);
  useEffect(() => {
    // on initial load check current selected affiliate if none then ignore
    const localSelectedAffiliate = localStorage.getItem('currentAffiliate');
    const localSelectedDataCompany = localStorage.getItem('currentDataCompany');
    if (localSelectedAffiliate && affiliates.length) {
      const idx = affiliates.findIndex(
        ({ customerIdName, dataCompanyId }) =>
          customerIdName === localSelectedAffiliate &&
          dataCompanyId.toString() === localSelectedDataCompany
      );
      setSelectedAffiliate(idx);
      dispatch(updateAffiliate(affiliates[idx]));
    }
  }, [affiliates, dispatch]);

  const contextValue = useMemo(() => {
    const setCurrentSelectedAffiliate = idx => {
      setSelectedAffiliate(idx);
      dispatch(updateAffiliate(affiliates[idx]));
    };

    return {
      affiliateRoles: affiliates.length
        ? affiliates.reduce(
            (acc, singleAffiliate) =>
              R.compose(
                R.union(acc),
                R.filter(perm => singleAffiliate[perm]),
                R.values
              )(affiliatePermissions),
            []
          )
        : [],
      affiliates,
      currentAffiliate,
      currentSelectedAffiliate,
      roles: profile?.groups,
      setCurrentSelectedAffiliate,
      userProfile: profile || {},
    };
  }, [affiliates, currentAffiliate, currentSelectedAffiliate, profile, dispatch]);

  if (isError && affiliates.length === 0 && currentSelectedAffiliate === null) {
    return (
      <Box>
        <Dialog
          maxWidth="xs"
          open
          slotProps={{
            paper: { elevation: 10 },
          }}
        >
          <DialogTitle color={theme.palette.text.primary} variant="h6">
            You do not have access to Swift.
          </DialogTitle>
          <DialogContent
            sx={{
              color: theme.palette.text.primary,
            }}
          >
            <Typography variant="body2">
              Please contact your company&apos;s Security Administrator for assistance.
              <br />
              <br />
            </Typography>
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: node.isRequired,
};
